/**
 * DrawerExamples.tsx
 * Comprehensive examples of HeroUI Drawer component
 * 
 * Showcases all HeroUI Drawer features using our shared components
 */

import React from 'react';
import { useDisclosure } from '@heroui/react';
import { PlaygroundPageLayout } from '../shared';
import { Drawer, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { 
  Menu, 
  Settings, 
  Bell, 
  User, 
  Filter,
  TrendingUp,
  BarChart3,
  Calendar
} from 'lucide-react';

export const DrawerExamples: React.FC = () => {
  // Different drawers for different examples
  const leftDrawer = useDisclosure();
  const rightDrawer = useDisclosure();
  const topDrawer = useDisclosure();
  const bottomDrawer = useDisclosure();
  const smallDrawer = useDisclosure();
  const largeDrawer = useDisclosure();
  const fullDrawer = useDisclosure();
  const blurDrawer = useDisclosure();
  const opaqueDrawer = useDisclosure();
  const navigationDrawer = useDisclosure();
  const settingsDrawer = useDisclosure();
  const filtersDrawer = useDisclosure();

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        {/* Basic Drawer Positions */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Drawer Placements</h3>
          <p className="text-text-secondary mb-6">
            Drawers can slide in from any side of the screen.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button onPress={leftDrawer.onOpen} className="flex items-center gap-2">
              <Menu className="w-4 h-4" />
              Left Drawer
            </Button>
            <Button onPress={rightDrawer.onOpen} className="flex items-center gap-2">
              <Menu className="w-4 h-4" />
              Right Drawer
            </Button>
            <Button onPress={topDrawer.onOpen} className="flex items-center gap-2">
              <Menu className="w-4 h-4" />
              Top Drawer
            </Button>
            <Button onPress={bottomDrawer.onOpen} className="flex items-center gap-2">
              <Menu className="w-4 h-4" />
              Bottom Drawer
            </Button>
          </div>
        </div>

        {/* Drawer Sizes */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Drawer Sizes</h3>
          <p className="text-text-secondary mb-6">
            Choose from different sizes to fit your content needs.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Button onPress={smallDrawer.onOpen} variant="bordered">
              Small (xs)
            </Button>
            <Button onPress={largeDrawer.onOpen} variant="bordered">
              Large (xl)
            </Button>
            <Button onPress={fullDrawer.onOpen} variant="bordered">
              Full Screen
            </Button>
          </div>
        </div>

        {/* Backdrop Variants */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Backdrop Effects</h3>
          <p className="text-text-secondary mb-6">
            Different backdrop styles for various visual effects.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Button onPress={blurDrawer.onOpen} variant="bordered">
              Blur Backdrop
            </Button>
            <Button onPress={opaqueDrawer.onOpen} variant="bordered">
              Opaque Backdrop
            </Button>
          </div>
        </div>

        {/* Real-world Examples */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Real-world Examples</h3>
          <p className="text-text-secondary mb-6">
            Common drawer patterns used in applications.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button onPress={navigationDrawer.onOpen} className="flex items-center gap-2">
              <Menu className="w-4 h-4" />
              Navigation Menu
            </Button>
            <Button onPress={settingsDrawer.onOpen} className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Settings Panel
            </Button>
            <Button onPress={filtersDrawer.onOpen} className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filters & Options
            </Button>
          </div>
        </div>

        {/* Drawer Implementations */}

        {/* Left Drawer */}
        <Drawer 
          isOpen={leftDrawer.isOpen} 
          onOpenChange={leftDrawer.onOpenChange}
          placement="left"
        >
          <DrawerContent>
            {(_onClose) => (
              <>
                <DrawerHeader>
                  <h4 className="text-lg font-semibold">Left Drawer</h4>
                </DrawerHeader>
                <DrawerBody>
                  <p className="text-text-secondary mb-4">
                    This drawer slides in from the left side of the screen.
                  </p>
                  <div className="space-y-3">
                    <div className="p-3 border border-border rounded-lg">
                      <h5 className="font-medium mb-1">Feature A</h5>
                      <p className="text-sm text-text-secondary">Description of feature A</p>
                    </div>
                    <div className="p-3 border border-border rounded-lg">
                      <h5 className="font-medium mb-1">Feature B</h5>
                      <p className="text-sm text-text-secondary">Description of feature B</p>
                    </div>
                  </div>
                </DrawerBody>
                <DrawerFooter>
                  <Button variant="light" onPress={_onClose}>
                    Cancel
                  </Button>
                  <Button color="primary" onPress={_onClose}>
                    Save Changes
                  </Button>
                </DrawerFooter>
              </>
            )}
          </DrawerContent>
        </Drawer>

        {/* Right Drawer */}
        <Drawer 
          isOpen={rightDrawer.isOpen} 
          onOpenChange={rightDrawer.onOpenChange}
          placement="right"
        >
          <DrawerContent>
            {(_onClose) => (
              <>
                <DrawerHeader>
                  <h4 className="text-lg font-semibold">Right Drawer</h4>
                </DrawerHeader>
                <DrawerBody>
                  <p className="text-text-secondary mb-4">
                    This drawer slides in from the right side (default placement).
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-surface rounded-lg">
                      <Bell className="w-5 h-5 text-primary" />
                      <div>
                        <h5 className="font-medium">Notifications</h5>
                        <p className="text-sm text-text-secondary">Manage your alerts</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-surface rounded-lg">
                      <User className="w-5 h-5 text-primary" />
                      <div>
                        <h5 className="font-medium">Profile</h5>
                        <p className="text-sm text-text-secondary">Update your information</p>
                      </div>
                    </div>
                  </div>
                </DrawerBody>
                <DrawerFooter>
                  <Button variant="light" onPress={_onClose}>
                    Close
                  </Button>
                </DrawerFooter>
              </>
            )}
          </DrawerContent>
        </Drawer>

        {/* Top Drawer */}
        <Drawer 
          isOpen={topDrawer.isOpen} 
          onOpenChange={topDrawer.onOpenChange}
          placement="top"
        >
          <DrawerContent>
            {(_onClose) => (
              <>
                <DrawerHeader>
                  <h4 className="text-lg font-semibold">Top Drawer</h4>
                </DrawerHeader>
                <DrawerBody>
                  <p className="text-text-secondary">
                    This drawer slides down from the top of the screen. Perfect for search bars, quick actions, or notifications.
                  </p>
                </DrawerBody>
              </>
            )}
          </DrawerContent>
        </Drawer>

        {/* Bottom Drawer */}
        <Drawer 
          isOpen={bottomDrawer.isOpen} 
          onOpenChange={bottomDrawer.onOpenChange}
          placement="bottom"
        >
          <DrawerContent>
            {(_onClose) => (
              <>
                <DrawerHeader>
                  <h4 className="text-lg font-semibold">Bottom Drawer</h4>
                </DrawerHeader>
                <DrawerBody>
                  <p className="text-text-secondary">
                    This drawer slides up from the bottom. Commonly used on mobile devices for actions or additional content.
                  </p>
                </DrawerBody>
                <DrawerFooter>
                  <Button variant="light" onPress={_onClose} className="w-full">
                    Got it
                  </Button>
                </DrawerFooter>
              </>
            )}
          </DrawerContent>
        </Drawer>

        {/* Small Drawer */}
        <Drawer 
          isOpen={smallDrawer.isOpen} 
          onOpenChange={smallDrawer.onOpenChange}
          size="xs"
        >
          <DrawerContent>
            {(_onClose) => (
              <>
                <DrawerHeader>
                  <h4 className="text-lg font-semibold">Small Drawer</h4>
                </DrawerHeader>
                <DrawerBody>
                  <p className="text-text-secondary text-sm">
                    Compact drawer for simple content or quick actions.
                  </p>
                </DrawerBody>
                <DrawerFooter>
                  <Button size="sm" onPress={_onClose}>
                    Close
                  </Button>
                </DrawerFooter>
              </>
            )}
          </DrawerContent>
        </Drawer>

        {/* Large Drawer */}
        <Drawer 
          isOpen={largeDrawer.isOpen} 
          onOpenChange={largeDrawer.onOpenChange}
          size="xl"
        >
          <DrawerContent>
            {(_onClose) => (
              <>
                <DrawerHeader>
                  <h4 className="text-lg font-semibold">Large Drawer</h4>
                </DrawerHeader>
                <DrawerBody>
                  <div className="space-y-6">
                    <div>
                      <h5 className="font-semibold mb-3">Trading Dashboard</h5>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-surface rounded-lg text-center">
                          <TrendingUp className="w-6 h-6 text-success mx-auto mb-2" />
                          <p className="font-medium">$2,457</p>
                          <p className="text-xs text-text-secondary">Total Profit</p>
                        </div>
                        <div className="p-4 bg-surface rounded-lg text-center">
                          <BarChart3 className="w-6 h-6 text-primary mx-auto mb-2" />
                          <p className="font-medium">47</p>
                          <p className="text-xs text-text-secondary">Trades</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="font-semibold mb-3">Recent Activity</h5>
                      <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="flex items-center gap-3 p-3 border border-border rounded-lg">
                            <Calendar className="w-4 h-4 text-text-tertiary" />
                            <div>
                              <p className="font-medium text-sm">Trade #{i}</p>
                              <p className="text-xs text-text-secondary">2 hours ago</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </DrawerBody>
                <DrawerFooter className="gap-2">
                  <Button variant="light" onPress={_onClose}>
                    Cancel
                  </Button>
                  <Button color="primary" onPress={_onClose}>
                    Export Data
                  </Button>
                </DrawerFooter>
              </>
            )}
          </DrawerContent>
        </Drawer>

        {/* Full Screen Drawer */}
        <Drawer 
          isOpen={fullDrawer.isOpen} 
          onOpenChange={fullDrawer.onOpenChange}
          size="full"
        >
          <DrawerContent>
            {(_onClose) => (
              <>
                <DrawerHeader className="border-b border-border">
                  <h4 className="text-xl font-semibold">Full Screen View</h4>
                </DrawerHeader>
                <DrawerBody className="p-8">
                  <div className="max-w-4xl mx-auto space-y-8">
                    <div className="text-center">
                      <h5 className="text-2xl font-bold mb-4">Full Screen Experience</h5>
                      <p className="text-text-secondary text-lg">
                        Perfect for detailed views, forms, or immersive content that needs maximum space.
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i} className="p-6 bg-surface rounded-lg border border-border">
                          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                            <BarChart3 className="w-6 h-6 text-primary" />
                          </div>
                          <h6 className="font-semibold mb-2">Feature {i}</h6>
                          <p className="text-text-secondary text-sm">
                            Detailed description of feature {i} with comprehensive information.
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </DrawerBody>
                <DrawerFooter className="border-t border-border">
                  <Button variant="light" onPress={_onClose}>
                    Close
                  </Button>
                  <Button color="primary" onPress={_onClose}>
                    Save Changes
                  </Button>
                </DrawerFooter>
              </>
            )}
          </DrawerContent>
        </Drawer>

        {/* Blur Backdrop Drawer */}
        <Drawer 
          isOpen={blurDrawer.isOpen} 
          onOpenChange={blurDrawer.onOpenChange}
          backdrop="blur"
        >
          <DrawerContent>
            {(_onClose) => (
              <>
                <DrawerHeader>
                  <h4 className="text-lg font-semibold">Blur Backdrop</h4>
                </DrawerHeader>
                <DrawerBody>
                  <p className="text-text-secondary">
                    This drawer uses a blur backdrop effect, creating a modern glass-like appearance while maintaining some visibility of the background content.
                  </p>
                </DrawerBody>
                <DrawerFooter>
                  <Button onPress={_onClose}>
                    Close
                  </Button>
                </DrawerFooter>
              </>
            )}
          </DrawerContent>
        </Drawer>

        {/* Opaque Backdrop Drawer */}
        <Drawer 
          isOpen={opaqueDrawer.isOpen} 
          onOpenChange={opaqueDrawer.onOpenChange}
          backdrop="opaque"
        >
          <DrawerContent>
            {(_onClose) => (
              <>
                <DrawerHeader>
                  <h4 className="text-lg font-semibold">Opaque Backdrop</h4>
                </DrawerHeader>
                <DrawerBody>
                  <p className="text-text-secondary">
                    This drawer uses an opaque backdrop (default), providing maximum focus on the drawer content by completely obscuring the background.
                  </p>
                </DrawerBody>
                <DrawerFooter>
                  <Button onPress={_onClose}>
                    Close
                  </Button>
                </DrawerFooter>
              </>
            )}
          </DrawerContent>
        </Drawer>

        {/* Navigation Drawer */}
        <Drawer 
          isOpen={navigationDrawer.isOpen} 
          onOpenChange={navigationDrawer.onOpenChange}
          placement="left"
          size="md"
        >
          <DrawerContent>
            {(_onClose) => (
              <>
                <DrawerHeader className="border-b border-border">
                  <h4 className="text-lg font-semibold flex items-center gap-2">
                    <Menu className="w-5 h-5" />
                    Navigation
                  </h4>
                </DrawerHeader>
                <DrawerBody className="p-0">
                  <nav className="space-y-1">
                    {[
                      { icon: TrendingUp, label: 'Dashboard', active: true },
                      { icon: BarChart3, label: 'Analytics', active: false },
                      { icon: Calendar, label: 'Schedule', active: false },
                      { icon: Settings, label: 'Settings', active: false },
                      { icon: User, label: 'Profile', active: false }
                    ].map((item, index) => (
                      <button
                        key={index}
                        className={`w-full flex items-center gap-3 px-6 py-3 text-left hover:bg-hover transition-colors ${
                          item.active ? 'bg-primary/10 text-primary border-r-2 border-primary' : 'text-text-secondary'
                        }`}
                        onClick={_onClose}
                      >
                        <item.icon className="w-5 h-5" />
                        {item.label}
                      </button>
                    ))}
                  </nav>
                </DrawerBody>
              </>
            )}
          </DrawerContent>
        </Drawer>

        {/* Settings Drawer */}
        <Drawer 
          isOpen={settingsDrawer.isOpen} 
          onOpenChange={settingsDrawer.onOpenChange}
          size="lg"
        >
          <DrawerContent>
            {(_onClose) => (
              <>
                <DrawerHeader>
                  <h4 className="text-lg font-semibold flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Settings
                  </h4>
                </DrawerHeader>
                <DrawerBody>
                  <div className="space-y-6">
                    <div>
                      <h5 className="font-semibold mb-3">Appearance</h5>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                          <span>Dark Mode</span>
                          <input type="checkbox" className="rounded" />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                          <span>Compact Layout</span>
                          <input type="checkbox" className="rounded" />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h5 className="font-semibold mb-3">Notifications</h5>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                          <div>
                            <p className="font-medium">Email Alerts</p>
                            <p className="text-sm text-text-secondary">Receive trade notifications</p>
                          </div>
                          <input type="checkbox" defaultChecked className="rounded" />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                          <div>
                            <p className="font-medium">Push Notifications</p>
                            <p className="text-sm text-text-secondary">Browser notifications</p>
                          </div>
                          <input type="checkbox" className="rounded" />
                        </div>
                      </div>
                    </div>
                  </div>
                </DrawerBody>
                <DrawerFooter>
                  <Button variant="light" onPress={_onClose}>
                    Cancel
                  </Button>
                  <Button color="primary" onPress={_onClose}>
                    Save Settings
                  </Button>
                </DrawerFooter>
              </>
            )}
          </DrawerContent>
        </Drawer>

        {/* Filters Drawer */}
        <Drawer 
          isOpen={filtersDrawer.isOpen} 
          onOpenChange={filtersDrawer.onOpenChange}
          size="md"
        >
          <DrawerContent>
            {(_onClose) => (
              <>
                <DrawerHeader>
                  <h4 className="text-lg font-semibold flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Filters & Options
                  </h4>
                </DrawerHeader>
                <DrawerBody>
                  <div className="space-y-6">
                    <div>
                      <h5 className="font-semibold mb-3">Date Range</h5>
                      <div className="grid grid-cols-2 gap-3">
                        <input 
                          type="date" 
                          className="p-2 border border-border rounded-lg bg-surface"
                          placeholder="Start Date"
                        />
                        <input 
                          type="date" 
                          className="p-2 border border-border rounded-lg bg-surface"
                          placeholder="End Date"
                        />
                      </div>
                    </div>

                    <div>
                      <h5 className="font-semibold mb-3">Categories</h5>
                      <div className="space-y-2">
                        {['Stocks', 'Options', 'Futures', 'Forex', 'Crypto'].map((category) => (
                          <label key={category} className="flex items-center gap-3 p-2">
                            <input type="checkbox" className="rounded" />
                            <span>{category}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h5 className="font-semibold mb-3">Status</h5>
                      <div className="space-y-2">
                        {['Open', 'Closed', 'Pending'].map((status) => (
                          <label key={status} className="flex items-center gap-3 p-2">
                            <input type="radio" name="status" className="rounded" />
                            <span>{status}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </DrawerBody>
                <DrawerFooter className="gap-2">
                  <Button variant="light" onPress={_onClose}>
                    Reset
                  </Button>
                  <Button color="primary" onPress={_onClose}>
                    Apply Filters
                  </Button>
                </DrawerFooter>
              </>
            )}
          </DrawerContent>
        </Drawer>
      </div>
    </PlaygroundPageLayout>
  );
};